/**
 * Analytics Dashboard (replaces the old Dashboard.jsx)
 * ----------------------------------------------------
 * Two goals:
 *   1. A clean KPI + trend overview focused on Daily Sales (invoice-backed)
 *      and Monthly Purchase (GRN-backed).
 *   2. A filterable Sales / Purchase reports section with Excel + PDF export.
 *
 * Backend contract lives in /app/backend/routes/dashboard_analytics.py.
 * Core modules (Invoice, GRN, Inventory, PO) are NOT touched — this file
 * is pure read-aggregation + UI.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  RefreshCw, Download, FileSpreadsheet, FileText, TrendingUp, TrendingDown, ShoppingCart,
  Receipt, Truck, Package, Activity, Loader2, Percent,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import OrderControlStatusCard from '../components/OrderControlStatusCard';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Date-range presets. All are resolved to `[startISO, endISO]` (inclusive).
// ---------------------------------------------------------------------------
const PRESETS = [
  { id: 'today',       label: 'Today' },
  { id: 'yesterday',   label: 'Yesterday' },
  { id: 'last_7',      label: 'Last 7 days' },
  { id: 'last_30',     label: 'Last 30 days' },
  { id: 'last_90',     label: 'Last 90 days' },
  { id: 'this_month',  label: 'This Month' },
  { id: 'last_month',  label: 'Last Month' },
  { id: 'custom',      label: 'Custom range' },
];

function resolvePreset(preset, customStart, customEnd) {
  const today = new Date(); today.setHours(0,0,0,0);
  const iso = (d) => d.toISOString().slice(0, 10);
  const shift = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (preset) {
    case 'today':      return { start: iso(today), end: iso(today) };
    case 'yesterday': {
      const y = shift(1);
      return { start: iso(y), end: iso(y) };
    }
    case 'last_7':     return { start: iso(shift(6)),  end: iso(today) };
    case 'last_30':    return { start: iso(shift(29)), end: iso(today) };
    case 'last_90':    return { start: iso(shift(89)), end: iso(today) };
    case 'this_month': {
      const ms = new Date(today); ms.setDate(1);
      return { start: iso(ms), end: iso(today) };
    }
    case 'last_month': {
      const ms = new Date(today); ms.setDate(1); ms.setMonth(ms.getMonth() - 1);
      const me = new Date(today); me.setDate(0); // last day of previous month
      return { start: iso(ms), end: iso(me) };
    }
    case 'custom':
      return { start: customStart || iso(today), end: customEnd || iso(today) };
    default:
      return { start: iso(today), end: iso(today) };
  }
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
const formatINR = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1_00_000) return `₹${(v / 1_00_000).toFixed(2)}L`;
  if (Math.abs(v) >= 1_000) return `₹${(v / 1_000).toFixed(1)}K`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};
const formatINRFull = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso || '').split('T')[0];
const isoToday = () => new Date().toISOString().slice(0, 10);
const isoMonthsAgo = (n) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ---------------------------------------------------------------------------
// Presentational bits
// ---------------------------------------------------------------------------
const KpiCard = ({ icon: Icon, label, value, sub, accent, testId, fullValue }) => (
  <div
    data-testid={testId}
    className={`bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition min-w-0 overflow-hidden`}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs uppercase tracking-wider text-slate-500 font-medium truncate">{label}</span>
      <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4 text-white" />
      </span>
    </div>
    <div
      className="mt-3 text-2xl font-bold text-slate-800 tabular-nums truncate"
      title={fullValue || (typeof value === 'string' ? value : undefined)}
    >
      {value}
    </div>
    {sub && <div className="text-xs text-slate-500 mt-1 truncate" title={sub}>{sub}</div>}
  </div>
);

const TrendCard = ({ title, subtitle, children, right }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
    <div className="px-5 pt-4 pb-2 flex items-start justify-between">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="px-2 pb-4 pt-1">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Sales / Purchase report tables — self-contained, filter-driven, paginated
// ---------------------------------------------------------------------------
function SalesReportPane({ franchises, products }) {
  const [startDate, setStartDate] = useState(isoMonthsAgo(1));
  const [endDate, setEndDate] = useState(isoToday());
  const [franchiseId, setFranchiseId] = useState('');
  const [productId, setProductId] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total_count: 0, total_value: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (startDate) p.set('start_date', startDate);
    if (endDate) p.set('end_date', endDate);
    if (franchiseId) p.set('franchise_id', franchiseId);
    if (productId) p.set('product_id', productId);
    return p.toString();
  }, [startDate, endDate, franchiseId, productId]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(qs);
      params.set('page', String(page));
      params.set('page_size', '25');
      const res = await axios.get(`${API}/analytics/sales-report?${params}`, authHeaders());
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load sales report');
    } finally {
      setLoading(false);
    }
  }, [qs, page]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const res = await axios.get(`${API}/analytics/sales-report/export.${kind}?${qs}`, {
        ...authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `sales_report.${kind}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Sales report (${kind.toUpperCase()}) downloaded`);
    } catch (e) {
      toast.error(`Export failed`);
    } finally {
      setExporting(null);
    }
  };

  const resetAndFetch = () => { setPage(1); };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); resetAndFetch(); }} data-testid="sales-filter-start" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); resetAndFetch(); }} data-testid="sales-filter-end" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Franchise / Customer</Label>
          <Select value={franchiseId || 'all'} onValueChange={(v) => { setFranchiseId(v === 'all' ? '' : v); resetAndFetch(); }}>
            <SelectTrigger data-testid="sales-filter-franchise"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All franchises</SelectItem>
              {franchises.map((f) => (
                <SelectItem key={f.id || f.name} value={f.id || f.name}>
                  {f.name} ({f.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Product</Label>
          <Select value={productId || 'all'} onValueChange={(v) => { setProductId(v === 'all' ? '' : v); resetAndFetch(); }}>
            <SelectTrigger data-testid="sales-filter-product"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All products</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name || p.sku}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleExport('xlsx')} disabled={!!exporting} data-testid="sales-export-xlsx">
            {exporting === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
          </Button>
          <CsvDownloadButton onDownload={() => handleExport('xlsx')} testId="sales-export-csv" disabled={!!exporting} />
          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleExport('pdf')} disabled={!!exporting} data-testid="sales-export-pdf">
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-slate-600">Rows: <strong className="text-slate-900">{data.total_count}</strong></span>
        <span className="text-slate-600">Total Value: <strong className="text-emerald-700">{formatINRFull(data.total_value)}</strong></span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[480px]">
          <table className="w-full text-sm" data-testid="sales-report-table">
            <thead className="bg-slate-50 border-b sticky top-0 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Invoice #</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Franchise / Customer</th>
                <th className="px-3 py-2 text-center">Items</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400">No sales in this range</td></tr>
              )}
              {!loading && data.items.map((r) => (
                <tr key={r.id} className="border-b hover:bg-indigo-50/30">
                  <td className="px-3 py-2 font-mono text-xs">{r.invoice_number}</td>
                  <td className="px-3 py-2">{fmtDate(r.invoice_date)}</td>
                  <td className="px-3 py-2">{r.franchise}</td>
                  <td className="px-3 py-2 text-center tabular-nums">{r.items_count}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 capitalize">{(r.status || '').replace('_', ' ')}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatINRFull(r.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.total_pages > 1 && (
          <div className="flex justify-between items-center text-xs px-3 py-2 bg-slate-50 border-t">
            <span className="text-slate-500">Page {page} of {data.total_pages}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
              <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PurchaseReportPane({ suppliers }) {
  const [startDate, setStartDate] = useState(isoMonthsAgo(1));
  const [endDate, setEndDate] = useState(isoToday());
  const [supplierId, setSupplierId] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total_count: 0, total_value: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (startDate) p.set('start_date', startDate);
    if (endDate) p.set('end_date', endDate);
    if (supplierId) p.set('supplier_id', supplierId);
    return p.toString();
  }, [startDate, endDate, supplierId]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(qs);
      params.set('page', String(page));
      params.set('page_size', '25');
      const res = await axios.get(`${API}/analytics/purchase-report?${params}`, authHeaders());
      setData(res.data);
    } catch {
      toast.error('Failed to load purchase report');
    } finally {
      setLoading(false);
    }
  }, [qs, page]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const res = await axios.get(`${API}/analytics/purchase-report/export.${kind}?${qs}`, {
        ...authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `purchase_report.${kind}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Purchase report (${kind.toUpperCase()}) downloaded`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const resetAndFetch = () => { setPage(1); };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); resetAndFetch(); }} data-testid="purchase-filter-start" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); resetAndFetch(); }} data-testid="purchase-filter-end" />
        </div>
        <div className="space-y-1 lg:col-span-2">
          <Label className="text-xs">Supplier</Label>
          <Select value={supplierId || 'all'} onValueChange={(v) => { setSupplierId(v === 'all' ? '' : v); resetAndFetch(); }}>
            <SelectTrigger data-testid="purchase-filter-supplier"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All suppliers</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id || s.name} value={s.id || s.name}>
                  {s.name} ({s.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleExport('xlsx')} disabled={!!exporting} data-testid="purchase-export-xlsx">
            {exporting === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
          </Button>
          <CsvDownloadButton onDownload={() => handleExport('xlsx')} testId="purchase-export-csv" disabled={!!exporting} />
          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleExport('pdf')} disabled={!!exporting} data-testid="purchase-export-pdf">
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <span className="text-slate-600">Rows: <strong className="text-slate-900">{data.total_count}</strong></span>
        <span className="text-slate-600">Total Value: <strong className="text-indigo-700">{formatINRFull(data.total_value)}</strong></span>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[480px]">
          <table className="w-full text-sm" data-testid="purchase-report-table">
            <thead className="bg-slate-50 border-b sticky top-0 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">GRN #</th>
                <th className="px-3 py-2 text-left">PO #</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Supplier</th>
                <th className="px-3 py-2 text-center">Items</th>
                <th className="px-3 py-2 text-right">Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400">No purchases in this range</td></tr>
              )}
              {!loading && data.items.map((r) => (
                <tr key={r.id} className="border-b hover:bg-indigo-50/30">
                  <td className="px-3 py-2 font-mono text-xs">{r.grn_number}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.po_number || '—'}</td>
                  <td className="px-3 py-2">{fmtDate(r.received_date)}</td>
                  <td className="px-3 py-2">{r.supplier_name}</td>
                  <td className="px-3 py-2 text-center tabular-nums">{r.items_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatINRFull(r.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.total_pages > 1 && (
          <div className="flex justify-between items-center text-xs px-3 py-2 bg-slate-50 border-t">
            <span className="text-slate-500">Page {page} of {data.total_pages}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
              <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Dashboard() {
  const [preset, setPreset] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const range = useMemo(() => resolvePreset(preset, customStart, customEnd), [preset, customStart, customEnd]);

  const [summary, setSummary] = useState({
    period_sales_value: 0, period_sales_count: 0, avg_order_value: 0,
    period_purchase_value: 0, period_purchase_count: 0, period_label: '',
    margin_value: 0, margin_pct: 0, margin_inward: 0, margin_outward: 0, margin_unmapped_items: 0,
  });
  const [salesTrend, setSalesTrend] = useState([]);
  const [purchaseTrend, setPurchaseTrend] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [ogStats, setOgStats] = useState(null);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    const qs = `?start_date=${range.start}&end_date=${range.end}`;
    try {
      const [sumRes, sTrend, pTrend, frRes, spRes, prRes, ogRes] = await Promise.all([
        axios.get(`${API}/analytics/summary${qs}`, authHeaders()),
        axios.get(`${API}/analytics/sales-trend${qs}`, authHeaders()),
        axios.get(`${API}/analytics/purchase-trend?months=12`, authHeaders()),
        axios.get(`${API}/analytics/franchises`, authHeaders()),
        axios.get(`${API}/analytics/suppliers`, authHeaders()),
        axios.get(`${API}/analytics/products`, authHeaders()),
        axios.get(`${API}/og/dashboard-stats`, authHeaders()).catch(() => ({ data: null })),
      ]);
      setSummary(sumRes.data);
      setSalesTrend(sTrend.data.series || []);
      setPurchaseTrend(pTrend.data.series || []);
      setFranchises(frRes.data || []);
      setSuppliers(spRes.data || []);
      setProducts(prRes.data || []);
      setOgStats(ogRes.data);
      setLastRefreshed(new Date());
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [range.start, range.end]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh every 60s when toggled on.
  const intervalRef = useRef(null);
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchAll(true), 60_000);
      return () => clearInterval(intervalRef.current);
    }
    return undefined;
  }, [autoRefresh, fetchAll]);

  const marginPct = Number(summary.margin_pct || 0);
  const marginIsPositive = marginPct >= 0;

  return (
    <div className="p-6 space-y-6 max-w-[1500px] mx-auto" data-testid="analytics-dashboard">
      {/* Order Acceptance status — visible at the very top so admins
          immediately see when stores are blocked. Read-only here; full
          controls live on /order-control. */}
      <OrderControlStatusCard context="erp" />

      {/* OG App KPIs — surfaced only if there's at least one OG retailer
          or order in the system. Hides cleanly for ERPs that don't use
          the OG integration. */}
      {ogStats && (ogStats.og_retailers?.total > 0 || ogStats.og_orders_today?.count > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="og-kpi-row">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-xs text-orange-700 font-medium uppercase tracking-wide">OG App Retailers</div>
            <div className="text-3xl font-bold text-orange-900 mt-1" data-testid="og-kpi-retailers-total">{ogStats.og_retailers?.total || 0}</div>
            <div className="text-xs text-orange-700/80 mt-1">
              {ogStats.og_retailers?.pending_approval || 0} pending approval
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-xs text-blue-700 font-medium uppercase tracking-wide">OG Orders Today</div>
            <div className="text-3xl font-bold text-blue-900 mt-1" data-testid="og-kpi-orders-today">{ogStats.og_orders_today?.count || 0}</div>
            <div className="text-xs text-blue-700/80 mt-1">
              ₹{Number(ogStats.og_orders_today?.revenue || 0).toLocaleString('en-IN')} revenue
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="text-xs text-emerald-700 font-medium uppercase tracking-wide">Credit Utilization</div>
            <div className="text-3xl font-bold text-emerald-900 mt-1" data-testid="og-kpi-credit-util">{ogStats.credit_utilization?.percentage || 0}%</div>
            <div className="text-xs text-emerald-700/80 mt-1">
              ₹{Number(ogStats.credit_utilization?.total_outstanding || 0).toLocaleString('en-IN')} / ₹{Number(ogStats.credit_utilization?.total_limit || 0).toLocaleString('en-IN')}
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-xs text-amber-700 font-medium uppercase tracking-wide">Documents to Verify</div>
            <div className="text-3xl font-bold text-amber-900 mt-1" data-testid="og-kpi-docs-verify">{ogStats.documents_to_verify || 0}</div>
            <div className="text-xs text-amber-700/80 mt-1">Retailers awaiting KYC review</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            Analytics Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Daily sales &amp; monthly purchase at a glance · Last refreshed {lastRefreshed.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-slate-600 mr-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              data-testid="auto-refresh-toggle"
            />
            Auto-refresh (60s)
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchAll()}
            disabled={refreshing}
            className="gap-1"
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Period selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3 shadow-sm">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Period</Label>
          <Select value={preset} onValueChange={(v) => setPreset(v)}>
            <SelectTrigger className="w-48" data-testid="dashboard-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {preset === 'custom' && (
          <>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">From</Label>
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-40"
                data-testid="dashboard-custom-start"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">To</Label>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-40"
                data-testid="dashboard-custom-end"
              />
            </div>
          </>
        )}
        <div className="ml-auto text-xs text-slate-500">
          Showing: <strong className="text-slate-900">{range.start} → {range.end}</strong>
        </div>
      </div>

      {/* KPI cards — 6 cards across on wide screens, 2×3 on medium */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard
          icon={TrendingUp}
          label="Sales (Period)"
          value={loading ? '—' : formatINR(summary.period_sales_value)}
          fullValue={loading ? '' : formatINRFull(summary.period_sales_value)}
          sub={`AOV: ${formatINR(summary.avg_order_value)}`}
          accent="bg-emerald-500"
          testId="kpi-period-sales"
        />
        <KpiCard
          icon={Receipt}
          label="Invoices (Period)"
          value={loading ? '—' : summary.period_sales_count}
          sub="Finalized / Paid"
          accent="bg-sky-500"
          testId="kpi-invoices-period"
        />
        <KpiCard
          icon={ShoppingCart}
          label="Purchase (Period)"
          value={loading ? '—' : formatINR(summary.period_purchase_value)}
          fullValue={loading ? '' : formatINRFull(summary.period_purchase_value)}
          sub="Via completed GRN"
          accent="bg-indigo-600"
          testId="kpi-period-purchase"
        />
        <KpiCard
          icon={Truck}
          label="GRNs (Period)"
          value={loading ? '—' : summary.period_purchase_count}
          sub="Completed receipts"
          accent="bg-violet-600"
          testId="kpi-grns-period"
        />
        <KpiCard
          icon={marginIsPositive ? TrendingUp : TrendingDown}
          label="Margin ₹"
          value={loading ? '—' : formatINR(summary.margin_value)}
          fullValue={loading ? '' : formatINRFull(summary.margin_value)}
          sub={`Cost: ${formatINR(summary.margin_inward)}${summary.margin_unmapped_items ? ` · ${summary.margin_unmapped_items} SKU w/o cost` : ''}`}
          accent={marginIsPositive ? 'bg-amber-500' : 'bg-rose-600'}
          testId="kpi-margin-value"
        />
        <KpiCard
          icon={Percent}
          label="Margin %"
          value={loading ? '—' : `${marginPct.toFixed(2)}%`}
          sub="(Sale − Cost) ÷ Sale"
          accent={marginIsPositive ? 'bg-teal-500' : 'bg-rose-600'}
          testId="kpi-margin-pct"
        />
      </div>

      {/* Trend charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TrendCard
          title={`Sales Trend · ${range.start} → ${range.end}`}
          subtitle="Based on finalized invoices · invoice_date bucket"
        >
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={salesTrend} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatINR} />
                <Tooltip
                  formatter={(v, name) => name === 'value' ? formatINRFull(v) : v}
                  labelStyle={{ fontSize: 12 }}
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#059669"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#059669' }}
                  activeDot={{ r: 5 }}
                  name="Sales"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </TrendCard>

        <TrendCard
          title="Purchase — Last 12 Months"
          subtitle="Based on completed GRNs · received_date bucket"
        >
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={purchaseTrend} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatINR} />
                <Tooltip
                  formatter={(v, name) => name === 'value' ? formatINRFull(v) : v}
                  labelStyle={{ fontSize: 12 }}
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="value" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Purchase" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TrendCard>
      </div>

      {/* Reports */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-slate-800">Reports</h2>
          <Download className="w-4 h-4 text-slate-400 ml-auto" />
          <span className="text-xs text-slate-500">Filter → Export Excel or PDF</span>
        </div>
        <Tabs defaultValue="sales">
          <TabsList className="mb-4" data-testid="reports-tabs">
            <TabsTrigger value="sales" data-testid="tab-sales">Sales Report</TabsTrigger>
            <TabsTrigger value="purchase" data-testid="tab-purchase">Purchase Report</TabsTrigger>
          </TabsList>
          <TabsContent value="sales">
            <SalesReportPane franchises={franchises} products={products} />
          </TabsContent>
          <TabsContent value="purchase">
            <PurchaseReportPane suppliers={suppliers} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
